import React, { useState, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { Character } from '../types/character';
import { themeColors } from '../types/template';
import { Eye, Edit2, FileJson, Download, Trash2, Star, StarOff, Lock, Shield, Sparkles, Share2 } from 'lucide-react';
import { CharacterEditor } from './CharacterEditor';
import html2canvas from 'html2canvas';
import { PublishButton } from './PublishButton';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from './LoadingSpinner';

const CharacterPreview = lazy(() => import('./CharacterPreview'));

interface Props {
  character: Character;
  onUpdate: (character: Character) => void;
  onDelete: (id: string) => void;
}

export function CharacterCard({ character, onUpdate, onDelete }: Props) {
  const [mode, setMode] = useState<'card' | 'preview' | 'edit'>('card');
  const [exportingPng, setExportingPng] = useState(false);
  const theme = themeColors[character.themeColor || 'purple'];
  const [isStarred, setIsStarred] = useState(character.isStarred || false);

  // Vérifier si l'utilisateur actuel est le propriétaire
  const [isOwner, setIsOwner] = useState<boolean | null>(null);

  React.useEffect(() => {
    const checkOwnership = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsOwner(user?.id === character.owner_id);
    };
    checkOwnership();
  }, [character.owner_id]);

  const handleUpdate = (updatedCharacter: Character) => {
    onUpdate(updatedCharacter);
  };

  const toggleStar = () => {
    const updatedCharacter = {
      ...character,
      isStarred: !isStarred
    };
    setIsStarred(!isStarred);
    onUpdate(updatedCharacter);
  };

  const downloadJson = () => {
    const exportCharacter = {
      ...character,
      profileImage: character.profileImage?.startsWith('blob:') ? '' : character.profileImage,
      backgroundImage: character.backgroundImage?.startsWith('blob:') ? '' : character.backgroundImage,
      illustrations: character.illustrations.map(ill => ({
        ...ill,
        url: ill.url?.startsWith('blob:') ? '' : ill.url
      }))
    };

    const dataStr = JSON.stringify(exportCharacter, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const fileName = `${character.name.toLowerCase().replace(/\s+/g, '-')}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', fileName);
    linkElement.click();
  };

  const exportToPng = async () => {
    if (exportingPng) return;
    setExportingPng(true);
    setMode('preview');

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const previewContainer = document.querySelector('.character-preview') as HTMLElement;
      if (!previewContainer) {
        throw new Error('Preview container not found');
      }

      const canvas = await html2canvas(previewContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `${character.name.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating PNG:', error);
      alert('Une erreur est survenue lors de la génération du PNG');
    } finally {
      setExportingPng(false);
      setMode('card');
    }
  };

  const previewText = character.sections[0]?.content || 'Aucune description...';

  if (mode === 'preview') {
    return (
      <motion.div
        className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="min-h-screen px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <Suspense fallback={<LoadingSpinner />}>
              <CharacterPreview 
                character={character} 
                onClose={() => setMode('card')}
              />
            </Suspense>
          </div>
        </div>
      </motion.div>
    );
  }

  if (mode === 'edit') {
    return (
      <CharacterEditor
        character={character}
        onUpdate={handleUpdate}
        onClose={() => setMode('card')}
      />
    );
  }

  return (
    <motion.div
      className={`bg-white/10 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/10 ${
        isStarred ? 'ring-2 ring-yellow-400/70' : ''
      } card-glow`}
    >
      {/* Banner avec shimmer */}
      <div className="relative h-44 overflow-hidden">
        {character.backgroundImage ? (
          <>
            <img
              src={character.backgroundImage}
              alt="Background"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110"
            />
            <div className={`absolute inset-0 ${theme.bg} opacity-60 mix-blend-multiply`} />
          </>
        ) : (
          <div className={`absolute inset-0 ${theme.bg} animate-shimmer`} />
        )}

        {/* Shimmer overlay */}
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{
            background: 'linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.05) 50%, transparent 80%)',
          }}
        />

        {/* Star button */}
        <motion.button
          onClick={toggleStar}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
            isStarred
              ? 'bg-yellow-400 text-white'
              : 'bg-white/15 text-white hover:bg-white/25'
          }`}
          title={isStarred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          {isStarred ? (
            <Star className="w-4 h-4 fill-current" />
          ) : (
            <StarOff className="w-4 h-4" />
          )}
        </motion.button>

        {/* Badge de propriete */}
        {character.owner_id && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="absolute top-3 left-3 px-3 py-1 glass-card rounded-full flex items-center gap-1.5"
          >
            {isOwner ? (
              <>
                <Shield className="w-3.5 h-3.5 text-green-300" />
                <span className="text-xs font-medium text-green-200">Proprietaire</span>
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5 text-gray-300" />
                <span className="text-xs font-medium text-gray-200">
                  {character.owner_email?.split('@')[0]}
                </span>
              </>
            )}
          </motion.div>
        )}

        {/* Profile image avec glow */}
        <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-14">
          <motion.div
            className={`w-28 h-28 rounded-full border-4 border-white/20 overflow-hidden shadow-2xl ${
              isStarred ? 'shadow-yellow-400/30' : 'shadow-black/20'
            }`}
            whileHover={{ scale: 1.05 }}
          >
            {character.profileImage ? (
              <img
                src={character.profileImage}
                alt={character.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center">
                <span className="text-3xl font-bold text-white/60">
                  {character.name.charAt(0)}
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 px-5 pb-5">
        <div className="text-center mb-5">
          <h2 className="text-lg font-bold text-white mb-1">{character.name}</h2>
          <p className="text-purple-200/60 text-sm line-clamp-2">
            {previewText}
          </p>
        </div>

        {/* Action buttons en grille compacte */}
        <div className="grid grid-cols-5 gap-2">
          <motion.button
            onClick={() => setMode('preview')}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="flex flex-col items-center gap-1 p-3 glass-card rounded-xl hover:bg-white/15 transition-colors group"
          >
            <Eye className="w-4 h-4 text-purple-300 group-hover:scale-110 transition-transform" />
            <span className="text-[10px] font-medium text-purple-200">Apercu</span>
          </motion.button>

          {(!character.owner_id || isOwner) && (
            <motion.button
              onClick={() => setMode('edit')}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="flex flex-col items-center gap-1 p-3 glass-card rounded-xl hover:bg-white/15 transition-colors group"
            >
              <Edit2 className="w-4 h-4 text-blue-300 group-hover:scale-110 transition-transform" />
              <span className="text-[10px] font-medium text-blue-200">Editer</span>
            </motion.button>
          )}

          <motion.button
            onClick={downloadJson}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="flex flex-col items-center gap-1 p-3 glass-card rounded-xl hover:bg-white/15 transition-colors group"
          >
            <FileJson className="w-4 h-4 text-amber-300 group-hover:scale-110 transition-transform" />
            <span className="text-[10px] font-medium text-amber-200">JSON</span>
          </motion.button>

          <motion.button
            onClick={exportToPng}
            disabled={exportingPng}
            whileHover={!exportingPng ? { scale: 1.05, y: -2 } : {}}
            whileTap={!exportingPng ? { scale: 0.95 } : {}}
            className={`flex flex-col items-center gap-1 p-3 glass-card rounded-xl transition-colors group
              ${exportingPng ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/15'}`}
          >
            <Download className="w-4 h-4 text-teal-300 group-hover:scale-110 transition-transform" />
            <span className="text-[10px] font-medium text-teal-200">PNG</span>
          </motion.button>

          {(!character.owner_id || isOwner) && (
            <motion.button
              onClick={() => onDelete(character.id)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="flex flex-col items-center gap-1 p-3 glass-card rounded-xl hover:bg-red-500/20 transition-colors group"
            >
              <Trash2 className="w-4 h-4 text-red-300 group-hover:scale-110 transition-transform" />
              <span className="text-[10px] font-medium text-red-200">Suppr</span>
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
}